/**
 * 통합관리자 — 세금계산서
 * ---------------------------------------------------------------------------
 * ## 하는 일
 * 계좌이체로 받는 기업에 세금계산서를 끊어 주어야 한다. 홈택스가 **엑셀
 * 일괄 등록**을 받아 주므로, 청구서에 이미 있는 숫자를 그 양식에 담아
 * 내려받게 한다. 손으로 옮겨 적다 틀리면 국세청에 잘못된 신고가 올라간다.
 *
 * ## 세 단계로 나눈다
 *   ① 받기    — 고른 청구서를 양식(xlsx)으로 내려받는다
 *   ② 올리기  — 홈택스에서 그 파일을 올려 발급한다 (여기 밖의 일이다)
 *   ③ 적기    — 돌아와서 승인번호를 적는다
 *
 * ②가 우리 손 밖이라 ①과 ③을 **따로** 표시한다. 하나로 합치면 "파일만
 * 받아 놓고 안 올린" 건이 발급된 것처럼 보인다.
 *
 * ## 자료가 모자라면 아예 못 담게 한다
 * 사업자등록번호·대표자·업태·종목이 비면 국세청이 그 줄만 튕겨 내는데,
 * 100줄 중 어느 줄인지 찾기가 어렵다. 담기 전에 막고 무엇이 비었는지 적는다.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { SupplierInfoPanel } from './SupplierInfoPanel';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

interface Issuer {
  bizRegNo: string;
  companyName: string;
  ceoName: string;
  address: string;
  bizType: string;
  bizItem: string;
  email: string;
  subBizNo: string;
  /** 비어 있어 양식을 만들 수 없는 칸 이름들 */
  missing: string[];
}

interface TaxRow {
  invoiceId: string;
  invoiceNo: string;
  tenantId: string;
  companyName: string;
  bizRegNo: string | null;
  ceoName: string | null;
  /** 양식의 「공급받는자 이메일1·2」로 들어갈 주소들 */
  emails: string[];
  periodStart: string;
  periodEnd: string;
  issuedAt: string | null;
  supplyAmount: number;
  vatAmount: number;
  totalAmount: number;
  paidAmount: number;
  fullyPaid: boolean;
  exportedAt: string | null;
  taxInvoiceNo: string | null;
  taxInvoiceAt: string | null;
  blocker: string | null;
}

/** 홈택스 목록에서 읽은 한 줄과 그 처리 결과 */
interface ImportRow {
  approvalNo: string;
  writtenOn: string;
  issuedOn: string | null;
  buyerBizNo: string;
  buyerName: string;
  supplyAmount: number;
  vatAmount: number;
  invoiceNoHint: string | null;
  invoiceNo: string | null;
  companyName: string | null;
  action: 'APPLIED' | 'ALREADY' | 'CONFLICT' | 'NOT_FOUND' | 'AMBIGUOUS';
  note: string | null;
}

interface ImportResult {
  dryRun: boolean;
  fileIssuerBizNo: string | null;
  total: number;
  applied: number;
  already: number;
  conflict: number;
  notFound: number;
  ambiguous: number;
  rows: ImportRow[];
}

const IMPORT_TAG: Record<ImportRow['action'], { text: string; color: string }> = {
  APPLIED: { text: '적용 대상', color: 'green' },
  ALREADY: { text: '이미 적힘', color: 'default' },
  CONFLICT: { text: '번호 충돌', color: 'red' },
  NOT_FOUND: { text: '못 찾음', color: 'orange' },
  AMBIGUOUS: { text: '모호함', color: 'volcano' },
};

const won = (v: number | null | undefined) =>
  `${Math.round(Number(v ?? 0)).toLocaleString('ko-KR')}원`;
const ymd = (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '-');
/** 1234567890 → 123-45-67890. 사람이 사업자등록증과 맞춰 볼 수 있게 */
const bizNo = (v: string | null) =>
  v && v.length === 10 ? `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}` : (v ?? '-');

export function TaxInvoicePanel({ onOpenLedger }: { onOpenLedger: (tenantId: string) => void }) {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [scope, setScope] = useState<'PENDING' | 'ALL'>('PENDING');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [issuerOpen, setIssuerOpen] = useState(false);
  const [issueTarget, setIssueTarget] = useState<TaxRow | null>(null);
  const [issuerForm] = Form.useForm<Issuer>();
  const [issueForm] = Form.useForm<{ taxInvoiceNo: string; issuedOn: Dayjs }>();

  const issuer = useQuery<Issuer, NormalizedError>({
    queryKey: ['admin', 'billing', 'tax-issuer'],
    queryFn: async () => (await apiClient.get('/admin/billing/tax-invoices/issuer')).data,
  });

  const params = {
    onlyPending: scope === 'PENDING' ? 'true' : 'false',
    from: range?.[0]?.format('YYYY-MM-DD'),
    to: range?.[1]?.format('YYYY-MM-DD'),
    limit: 300,
  };

  const rows = useQuery<TaxRow[], NormalizedError>({
    queryKey: ['admin', 'billing', 'tax-invoices', params],
    queryFn: async () => (await apiClient.get('/admin/billing/tax-invoices', { params })).data,
  });

  const saveIssuer = useMutation<Issuer, NormalizedError, Partial<Issuer>>({
    mutationFn: async (v) =>
      (await apiClient.patch('/admin/billing/tax-invoices/issuer', v)).data,
    onSuccess: () => {
      message.success('공급자 정보를 저장했습니다.');
      setIssuerOpen(false);
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'tax-issuer'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  const markIssued = useMutation<void, NormalizedError, { invoiceId: string; taxInvoiceNo: string; issuedOn: string }>({
    mutationFn: async (v) => {
      await apiClient.patch(`/admin/billing/tax-invoices/${v.invoiceId}`, {
        taxInvoiceNo: v.taxInvoiceNo,
        issuedOn: v.issuedOn,
      });
    },
    onSuccess: () => {
      message.success('승인번호를 적었습니다.');
      setIssueTarget(null);
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'tax-invoices'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  const pickedRows = (rows.data ?? []).filter((r) => picked.includes(r.invoiceId));
  const supplyTotal = pickedRows.reduce((s, r) => s + r.supplyAmount, 0);
  const vatTotal = pickedRows.reduce((s, r) => s + r.vatAmount, 0);

  /**
   * 양식을 내려받는다.
   *
   * 파일로 내려오므로 react-query 의 캐시를 태우지 않는다. 오류일 때는
   * 서버가 JSON 을 주는데, blob 으로 받으면 글자가 안 보인다 — 그래서
   * blob 을 글자로 되돌려 사유를 꺼내 보여 준다.
   */
  /* ---- 승인번호 일괄 등록 ---- */
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importText, setImportText] = useState('');

  /**
   * 파일을 읽어 **미리보기만** 받는다.
   *
   * 홈택스 목록은 UTF-8 탭 구분 글자 파일이다. 읽기만 브라우저에서 하고
   * 해석은 서버가 한다 — 규칙이 두 곳에 흩어지면 어긋난다.
   */
  const preview = async (file: File) => {
    setImporting(true);
    try {
      const text = await file.text();
      if (text.length > 900_000) {
        message.error('파일이 너무 큽니다. 기간을 나눠 내려받아 주세요.');
        return;
      }
      setImportText(text);
      const res = await apiClient.post('/admin/billing/tax-invoices/import', {
        content: text,
        dryRun: true,
      });
      setImportResult(res.data as ImportResult);
    } catch (e) {
      message.error(errorText(e as NormalizedError));
    } finally {
      setImporting(false);
    }
  };

  const applyImport = async () => {
    setImporting(true);
    try {
      const res = await apiClient.post('/admin/billing/tax-invoices/import', {
        content: importText,
        dryRun: false,
      });
      const r = res.data as ImportResult;
      message.success(`승인번호 ${r.applied}건을 적었습니다.`);
      setImportResult(null);
      setImportText('');
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'tax-invoices'] });
    } catch (e) {
      message.error(errorText(e as NormalizedError));
    } finally {
      setImporting(false);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.post(
        '/admin/billing/tax-invoices/export',
        { invoiceIds: picked },
        { responseType: 'blob' },
      );
      const disp = String(res.headers['content-disposition'] ?? '');
      const star = /filename\*=UTF-8''([^;]+)/.exec(disp);
      const name = star ? decodeURIComponent(star[1]!) : '홈택스_세금계산서.xlsx';

      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);

      message.success(`${picked.length}건을 담은 양식을 내려받았습니다.`);
      setPicked([]);
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'tax-invoices'] });
    } catch (err) {
      const blob = (err as { response?: { data?: Blob } }).response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          message.error(JSON.parse(text).message ?? text);
        } catch {
          message.error(text);
        }
      } else {
        message.error(errorText(err as NormalizedError));
      }
    } finally {
      setDownloading(false);
    }
  };

  const columns: ColumnsType<TaxRow> = [
    {
      title: '기업',
      key: 'company',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
            onClick={() => onOpenLedger(r.tenantId)}>
            {r.companyName}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>{bizNo(r.bizRegNo)}</Text>
        </Space>
      ),
    },
    { title: '청구서', dataIndex: 'invoiceNo', key: 'no', width: 160 },
    {
      /*
        양식의 「공급받는자 이메일1·2」로 들어갈 주소. 기업이 「청구 문서
        수신」으로 지정한 사람들이고, 지정이 없으면 소유자·관리자다.
        양식이 두 칸만 받으므로 세 번째부터는 들어가지 않는다 — 그 사실을
        담기 전에 보여 준다.
      */
      title: '계산서 받을 메일',
      key: 'emails',
      width: 220,
      render: (_, r) => {
        const es = r.emails ?? [];
        if (es.length === 0) return <Text type="danger" style={{ fontSize: 12 }}>없음</Text>;
        return (
          <Space direction="vertical" size={0}>
            {es.slice(0, 2).map((e, i) => (
              <Text key={e} style={{ fontSize: 12 }}>
                <Text type="secondary">{i + 1}.</Text> {e}
              </Text>
            ))}
            {es.length > 2 && (
              <Tooltip title={`양식은 두 칸까지라 ${es.slice(2).join(', ')} 은(는) 들어가지 않습니다`}>
                <Text type="secondary" style={{ fontSize: 12 }}>외 {es.length - 2}명(제외)</Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '작성일자',
      key: 'issued',
      width: 110,
      render: (_, r) => ymd(r.issuedAt),
    },
    {
      title: '공급가액',
      key: 'supply',
      width: 120,
      align: 'right',
      render: (_, r) => won(r.supplyAmount),
    },
    { title: '세액', key: 'vat', width: 110, align: 'right', render: (_, r) => won(r.vatAmount) },
    {
      title: '구분',
      key: 'receipt',
      width: 90,
      render: (_, r) => (
        <Tooltip title={r.fullyPaid ? '돈을 받았으므로 영수(01)' : '아직 못 받았으므로 청구(02)'}>
          <Tag color={r.fullyPaid ? 'green' : 'gold'}>{r.fullyPaid ? '영수' : '청구'}</Tag>
        </Tooltip>
      ),
    },
    {
      /*
        상태는 **세 단계 중 어디까지 왔는가**다. 꼬리표만으로는 뜻이 바로
        오지 않아(실제로 "파일 받음이 뭐냐"는 물음을 받았다) 한 줄 설명을
        아래에 붙이고, 자세한 것은 올려놓은 말풍선에 둔다.
      */
      title: '진행 상태',
      key: 'state',
      width: 230,
      render: (_, r) =>
        r.blocker ? (
          <Tooltip
            title={`${r.blocker} — 이대로 올리면 국세청이 이 줄을 거절합니다. 기업 정보를 채운 뒤 다시 시도하세요.`}
          >
            <Space direction="vertical" size={0}>
              <Tag color="red">기업 정보 부족</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>{r.blocker}</Text>
            </Space>
          </Tooltip>
        ) : r.taxInvoiceNo ? (
          <Space direction="vertical" size={0}>
            <Tag color="green">발급 완료</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.taxInvoiceNo} · {ymd(r.taxInvoiceAt)}
            </Text>
          </Space>
        ) : r.exportedAt ? (
          <Tooltip title="양식 파일에 담아 내려받았습니다. 홈택스에 올려 발급한 뒤 승인번호를 적어 주세요.">
            <Space direction="vertical" size={0}>
              <Tag color="blue">양식 내려받음</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {ymd(r.exportedAt)} · 홈택스 발급 대기
              </Text>
            </Space>
          </Tooltip>
        ) : (
          <Tooltip title="아직 양식 파일에 담지 않았습니다.">
            <Tag>발급 전</Tag>
          </Tooltip>
        ),
    },
    {
      title: '관리',
      key: 'act',
      width: 160,
      render: (_, r) =>
        r.taxInvoiceNo ? (
          <Button size="small" type="link" danger
            onClick={() => void apiClient
              .delete(`/admin/billing/tax-invoices/${r.invoiceId}`)
              .then(() => {
                message.success('승인번호를 지웠습니다.');
                void client.invalidateQueries({ queryKey: ['admin', 'billing', 'tax-invoices'] });
              })
              .catch((e) => message.error(errorText(e as NormalizedError)))}>
            번호 지우기
          </Button>
        ) : (
          <Button size="small" type="link" disabled={Boolean(r.blocker)}
            onClick={() => {
              setIssueTarget(r);
              issueForm.setFieldsValue({ taxInvoiceNo: '', issuedOn: dayjs() });
            }}>
            홈택스 승인번호입력
          </Button>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/*
        입금 계좌와 증빙 서류.
        공급자 정보 바로 위에 둔다 — 「계산서를 끊으려면 무엇이 있어야 하나」를
        한자리에서 보게 하려는 것이다. 셋 다 고객에게 나가는 값이다.
      */}
      <SupplierInfoPanel />

      {/* ---- 공급자(우리) 정보 ---- */}
      <Card
        size="small"
        title="공급자 정보 (우리 회사)"
        extra={
          <Button size="small" onClick={() => {
            issuerForm.setFieldsValue(issuer.data ?? ({} as Issuer));
            setIssuerOpen(true);
          }}>
            고치기
          </Button>
        }
      >
        {issuer.data && issuer.data.missing.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message={`${issuer.data.missing.join(' · ')} 이(가) 비어 있어 양식을 만들 수 없습니다`}
            description="사업자등록증에 적힌 그대로 넣어 주세요. 짐작으로 채우면 국세청이 거절하거나 잘못된 계산서가 나갑니다."
          />
        )}
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="등록번호">{bizNo(issuer.data?.bizRegNo ?? null)}</Descriptions.Item>
          <Descriptions.Item label="상호">{issuer.data?.companyName || '-'}</Descriptions.Item>
          <Descriptions.Item label="대표자">{issuer.data?.ceoName || <Text type="danger">입력 필요</Text>}</Descriptions.Item>
          <Descriptions.Item label="업태">{issuer.data?.bizType || <Text type="danger">입력 필요</Text>}</Descriptions.Item>
          <Descriptions.Item label="종목">{issuer.data?.bizItem || <Text type="danger">입력 필요</Text>}</Descriptions.Item>
          <Descriptions.Item label="이메일">{issuer.data?.email || '-'}</Descriptions.Item>
          <Descriptions.Item label="주소" span={3}>{issuer.data?.address || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Alert
        type="info"
        showIcon
        message="세 단계로 진행합니다"
        description={
          <>
            <b>①</b> 고른 청구서를 <b>국세청 공식 양식(100건까지)</b> 그대로 담아 내려받습니다
            → <b>②</b> 홈택스 <b>「전자(세)계산서 → 엑셀 일괄 발급」</b> 에 그 파일을 올려
            발급합니다 → <b>③</b> 홈택스에서 <b>「매출 전자세금계산서 목록」</b> 을 txt 로
            내려받아 아래 <b>[홈택스 목록으로 일괄 등록]</b> 에 올리시면 승인번호가 한 번에
            적힙니다.
            <br />
            ②가 우리 손 밖이라 ①과 ③을 따로 표시합니다 —{' '}
            <Tag color="blue" style={{ marginInline: 2 }}>양식 내려받음</Tag> 은 파일만 받고
            아직 홈택스에 올리지 않은 상태,{' '}
            <Tag color="red" style={{ marginInline: 2 }}>기업 정보 부족</Tag> 은 그 기업의
            사업자등록번호·대표자·주소·업태·종목·수신 이메일 중 비어 있는 것이 있어{' '}
            <b>양식에 넣지 못하는</b> 상태입니다.
            <br />
            <b>계산서 받을 메일</b>은 그 기업이 <b>구성원 → 청구 문서 수신</b> 으로
            지정한 분들입니다. 지정이 없으면 그 기업의 소유자·관리자가 들어갑니다.
          </>
        }
      />

      <Space wrap align="center">
        <Segmented
          value={scope}
          onChange={(v) => { setScope(v as 'PENDING' | 'ALL'); setPicked([]); }}
          options={[
            { value: 'PENDING', label: '미발급만' },
            { value: 'ALL', label: '전체' },
          ]}
        />
        <RangePicker
          value={range}
          onChange={(v) => setRange((v as [Dayjs, Dayjs] | null) ?? null)}
          allowEmpty={[true, true]}
          placeholder={['발행일 시작', '발행일 끝']}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void rows.refetch()} />
        <Text type="secondary">
          {(rows.data ?? []).length}건 · 고른 {pickedRows.length}건 ·
          {' '}공급가액 <b>{won(supplyTotal)}</b> · 세액 <b>{won(vatTotal)}</b>
        </Text>
      </Space>

      {rows.isError && <Alert type="error" showIcon message={errorText(rows.error)} />}

      <Table<TaxRow>
        rowKey="invoiceId"
        size="small"
        loading={rows.isLoading}
        dataSource={rows.data ?? []}
        columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: '세금계산서를 끊을 청구서가 없습니다.' }}
        rowSelection={{
          selectedRowKeys: picked,
          onChange: (keys) => setPicked(keys as string[]),
          // 자료가 모자란 줄은 담을 수 없다. 담기면 그 줄 때문에 파일 전체가 막힌다.
          getCheckboxProps: (r) => ({ disabled: Boolean(r.blocker) }),
        }}
      />

      <Space wrap>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          disabled={
            picked.length === 0 || (issuer.data?.missing.length ?? 1) > 0 || picked.length > 100
          }
          onClick={() => void download()}
        >
          ① 홈택스 양식 받기 ({picked.length}건)
        </Button>

        {/*
          홈택스 목록 파일을 고르면 **바로 적용하지 않고** 미리보기를 먼저
          띄운다. 세금에 관한 일이라 눌러 놓고 결과를 보는 순서가 되면 안 된다.
        */}
        <input
          ref={fileRef}
          type="file"
          accept=".txt,text/plain"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void preview(f);
          }}
        />
        <Button
          icon={<UploadOutlined />}
          loading={importing}
          onClick={() => fileRef.current?.click()}
        >
          ③ 홈택스 목록으로 일괄 등록
        </Button>

        {picked.length > 100 && (
          <Text type="danger">한 파일에 100건까지입니다. 기간을 나눠 받아 주세요.</Text>
        )}
      </Space>

      {/* ---- 일괄 등록 미리보기 ---- */}
      <Modal
        open={importResult !== null}
        title="승인번호 일괄 등록 — 미리보기"
        width={900}
        onCancel={() => setImportResult(null)}
        footer={[
          <Button key="c" onClick={() => setImportResult(null)}>닫기</Button>,
          <Button
            key="ok"
            type="primary"
            loading={importing}
            disabled={(importResult?.rows.filter((r) => r.action === 'APPLIED').length ?? 0) === 0}
            onClick={() => void applyImport()}
          >
            {importResult?.rows.filter((r) => r.action === 'APPLIED').length ?? 0}건 적용
          </Button>,
        ]}
      >
        {importResult && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={5} bordered>
              <Descriptions.Item label="읽은 줄">{importResult.total}건</Descriptions.Item>
              <Descriptions.Item label="적용 대상">
                <Text strong>{importResult.rows.filter((r) => r.action === 'APPLIED').length}건</Text>
              </Descriptions.Item>
              <Descriptions.Item label="이미 적힘">{importResult.already}건</Descriptions.Item>
              <Descriptions.Item label="번호 충돌">
                <Text type={importResult.conflict > 0 ? 'danger' : undefined}>
                  {importResult.conflict}건
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="못 찾음">
                {importResult.notFound + importResult.ambiguous}건
              </Descriptions.Item>
            </Descriptions>

            {(importResult.conflict > 0 || importResult.ambiguous > 0) && (
              <Alert
                type="warning"
                showIcon
                message="사람이 확인해야 하는 줄이 있습니다"
                description="「번호 충돌」은 그 청구서에 이미 다른 승인번호가 적혀 있다는 뜻이고, 「모호함」은 같은 조건의 청구서가 둘 이상이라는 뜻입니다. 짐작으로 붙이지 않습니다 — 아래 사유를 보고 직접 정해 주세요."
              />
            )}

            <Table
              rowKey="approvalNo"
              size="small"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              dataSource={importResult.rows}
              columns={[
                { title: '작성일자', dataIndex: 'writtenOn', key: 'w', width: 100 },
                { title: '승인번호', dataIndex: 'approvalNo', key: 'a', width: 230 },
                {
                  title: '공급받는자',
                  key: 'b',
                  render: (_: unknown, r: ImportRow) => (
                    <Space direction="vertical" size={0}>
                      <Text>{r.companyName ?? r.buyerName}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{bizNo(r.buyerBizNo)}</Text>
                    </Space>
                  ),
                },
                {
                  title: '공급가액 / 세액',
                  key: 'm',
                  width: 170,
                  align: 'right',
                  render: (_: unknown, r: ImportRow) =>
                    `${won(r.supplyAmount)} / ${won(r.vatAmount)}`,
                },
                {
                  title: '결과',
                  key: 'r',
                  width: 230,
                  render: (_: unknown, r: ImportRow) => (
                    <Space direction="vertical" size={0}>
                      <Tag color={IMPORT_TAG[r.action].color}>{IMPORT_TAG[r.action].text}</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.invoiceNo ? `${r.invoiceNo} · ` : ''}{r.note ?? ''}
                      </Text>
                    </Space>
                  ),
                },
              ]}
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              청구서를 찾는 순서는 ① 홈택스 <b>비고</b> 에 남아 있는 청구서 번호,
              ② 그것이 없으면 <b>사업자등록번호 · 작성일자 · 공급가액 · 세액</b> 네 가지가
              모두 같은 청구서입니다. 둘 다 아니면 「못 찾음」으로 둡니다.
            </Paragraph>
          </Space>
        )}
      </Modal>

      {/* ---- 공급자 정보 고치기 ---- */}
      <Modal
        open={issuerOpen}
        title="공급자 정보"
        okText="저장"
        cancelText="취소"
        confirmLoading={saveIssuer.isPending}
        onCancel={() => setIssuerOpen(false)}
        onOk={() => void issuerForm.validateFields().then((v) => saveIssuer.mutate(v))}
        width={560}
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          사업자등록증에 적힌 그대로 넣어 주세요. 이 값이 발급되는 모든 세금계산서의
          공급자 칸에 그대로 들어갑니다.
        </Paragraph>
        <Form form={issuerForm} layout="vertical" size="small">
          <Form.Item name="bizRegNo" label="사업자등록번호"
            rules={[{ required: true, message: '숫자 10자리' }]}>
            <Input placeholder="5858800533 (- 없이)" maxLength={12} />
          </Form.Item>
          <Form.Item name="companyName" label="상호" rules={[{ required: true }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="ceoName" label="대표자 성명" rules={[{ required: true }]}>
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item name="address" label="사업장 주소" rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="bizType" label="업태" rules={[{ required: true }]}
            extra="사업자등록증의 「업태」 란 (예: 정보통신업)">
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item name="bizItem" label="종목" rules={[{ required: true }]}
            extra="사업자등록증의 「종목」 란 (예: 소프트웨어 개발 및 공급업)">
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item name="email" label="이메일" rules={[{ required: true }]}>
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="subBizNo" label="종사업장번호"
            extra="사업자단위과세가 아니면 비워 두세요">
            <Input maxLength={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- 승인번호 적기 ---- */}
      <Modal
        open={issueTarget !== null}
        title={`승인번호 — ${issueTarget?.companyName ?? ''}`}
        okText="저장"
        cancelText="취소"
        confirmLoading={markIssued.isPending}
        onCancel={() => setIssueTarget(null)}
        onOk={() =>
          void issueForm.validateFields().then((v) =>
            markIssued.mutate({
              invoiceId: issueTarget!.invoiceId,
              taxInvoiceNo: v.taxInvoiceNo,
              issuedOn: v.issuedOn.format('YYYY-MM-DD'),
            }),
          )
        }
        width={460}
      >
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
          <Descriptions.Item label="청구서">{issueTarget?.invoiceNo}</Descriptions.Item>
          <Descriptions.Item label="공급가액 / 세액">
            {won(issueTarget?.supplyAmount)} / {won(issueTarget?.vatAmount)}
          </Descriptions.Item>
        </Descriptions>
        <Form form={issueForm} layout="vertical" size="small">
          <Form.Item name="taxInvoiceNo" label="국세청 승인번호" rules={[{ required: true }]}
            extra="홈택스 발급 내역에서 복사해 붙여 넣으세요">
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item name="issuedOn" label="발급일" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} allowClear={false} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
